import React, { useState } from 'react';
import MerkleTree from './MerkleTree';

function TransactionProof({ agreementId, proof, compact = true }) {
  const [isExpanded, setIsExpanded] = useState(!compact);
  const [copied, setCopied] = useState(null);

  if (!proof) return null;

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(field);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const short = (hash) => {
    if (!hash) return 'N/A';
    return `${hash.substring(0, 12)}...${hash.substring(hash.length - 8)}`;
  };

  const rows = [
    ['Transaction ID', proof.txID, 'txID'],
    ['Block', `#${proof.blockNumber}`],
    ['Block Hash', proof.blockHash, 'hash'],
    ['Previous Hash', proof.previousHash],
    ['Position in Block', `Tx #${proof.txIndex + 1}`],
    ['Timestamp', proof.blockTimestamp ? new Date(proof.blockTimestamp).toLocaleString() : null],
    ['Channel', proof.channelName],
    ['Validation', proof.validationCode],
  ].filter(([, v]) => v != null);

  return (
    <div style={{
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      overflow: 'hidden',
      background: 'white',
      marginBottom: '10px'
    }}>
      {/* Header row */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '10px 14px', cursor: 'pointer', userSelect: 'none',
          background: '#fafafa', borderBottom: isExpanded ? '1px solid #e0e0e0' : 'none'
        }}
      >
        <span style={{ fontWeight: '600', fontSize: '0.88rem', color: '#333' }}>
          Blockchain Proof
          {proof.validationCode === 'VALID' && (
            <span style={{ marginLeft: '10px', color: '#276749', fontWeight: '400', fontSize: '0.82rem' }}>
              Verified
            </span>
          )}
        </span>
        <span style={{ color: '#888', fontSize: '0.82rem' }}>{isExpanded ? 'Collapse' : 'Expand'}</span>
      </div>

      {isExpanded && (
        <div style={{ padding: '12px 14px' }}>
          {/* Data table */}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', marginBottom: '12px' }}>
            <tbody>
              {rows.map(([label, value, copyKey]) => (
                <tr key={label} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '5px 8px', color: '#666', fontWeight: '600', width: '160px', whiteSpace: 'nowrap' }}>{label}</td>
                  <td style={{ padding: '5px 8px', fontFamily: copyKey ? 'monospace' : 'inherit', color: '#333' }}>
                    {copyKey ? short(value) : value}
                    {copyKey && value && (
                      <button
                        onClick={() => copyToClipboard(value, copyKey)}
                        style={{
                          marginLeft: '8px', padding: '1px 6px', fontSize: '0.72rem',
                          background: copied === copyKey ? '#d4edda' : '#f0f0f0',
                          border: '1px solid #ccc', borderRadius: '3px', cursor: 'pointer', color: '#444'
                        }}
                      >
                        {copied === copyKey ? 'Copied' : 'Copy'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Endorsements */}
          {proof.endorsements && proof.endorsements.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '0.78rem', color: '#666', fontWeight: '600', marginBottom: '4px' }}>Endorsements</div>
              {proof.endorsements.map((e, i) => (
                <div key={i} style={{ fontSize: '0.78rem', color: '#444', fontFamily: 'monospace', padding: '2px 0' }}>
                  {e.mspId} — {e.signature}
                </div>
              ))}
            </div>
          )}

          {/* In-block Merkle tree */}
          {proof.txMerkleTree && (
            <MerkleTree merkleTree={proof.txMerkleTree} txID={proof.txID} />
          )}
        </div>
      )}
    </div>
  );
}

export default TransactionProof;
